import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
  AccountCodeService,
  EventService,
  JobsService,
  OrderService,
  OrderTagService,
  OrganisationsService,
  UserService,
} from '../../../../services';
import {
  JobScreenOrdersJob,
  JobScreenOrdersOrder,
  JobScreenOrdersToOrderTag,
} from '../../../../interfaces/screen-specific/job-screen';
import { JobStatus } from '../../../../enums/job-status';
import { OrderStatus } from '../../../job-control/job-screen.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Message } from '../../../../classes/Message';
import { MessageService, SelectItem } from 'primeng';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';
import { IUser } from '../../../../interfaces/user';
import { InputHelper } from '../../../../utils/input.helper';
import { JobScreenCommonService } from '../job-screen-common.service';
import { Subscription } from 'rxjs';
import { RefreshJobEventTypeEnum } from '../../../../enums/refresh-job-event-type.enum';
import { TaskType } from '../../../../enums/task.type';
import { flatMap } from 'lodash';
import { ICancellationStatus } from '../../../../interfaces/cancellation-status';
import { first } from 'rxjs/operators';

const defaultSelectItem: SelectItem = {
  label: 'Please Select',
  value: null,
};

@Component({
  selector: 'job-screen-orders',
  templateUrl: './job-screen-orders.component.html',
  styleUrls: ['./job-screen-orders.component.scss', '../job-screen-common.scss'],
})
export class JobScreenOrdersComponent implements OnInit, OnDestroy, OnChanges {
  @Input() jobId!: number;
  @Input('organisationId') organisationId!: number;
  @Input('organisationTypeId') organisationTypeId!: number;

  job: JobScreenOrdersJob;

  _selectedOrder: JobScreenOrdersOrder = null;
  private cancellationStatusOptions: SelectItem[];
  creatingInvoice: boolean;

  public get selectedOrder(): JobScreenOrdersOrder {
    return this._selectedOrder;
  }

  public set selectedOrder(value: JobScreenOrdersOrder) {
    this._selectedOrder = value;
    this.eventService.JobScreenEvent.emit({
      event: 'order-changed',
      data: { orderId: this._selectedOrder?.id || null },
    });
  }

  jobStatus = JobStatus;
  currentOrder: JobScreenOrdersOrder;
  showBookingInfo: boolean;
  selectedImpersonateUser: null;
  showImpersonateCustomerDialog: boolean;
  impersonateCustomerUsers: any;
  purchaseOrderNo: string;
  accountCodes: SelectItem[];
  orderTags: SelectItem[];

  showCancelOrderDialog: boolean;

  order: JobScreenOrdersOrder;

  subscriptions: Subscription[] = [];

  isBusy: boolean;
  currentUser: IUser;

  showEditOrderDialog: boolean = false;

  public orderSequencingEnabled: boolean;
  public taskSequenceEditorVisible: boolean;

  directedFromCombine: boolean = false;
  showCopyOrderDialog: boolean = false;
  showQuickCopyOrderDialog: boolean = false;
  loadingQuickCopy: boolean = false;

  quickCopyOrder: JobScreenOrdersOrder;

  isImpersonating: boolean = false;

  constructor(
    private jobService: JobsService,
    private eventService: EventService,
    private userService: UserService,
    private router: Router,
    public messageService: MessageService,
    private orderService: OrderService,
    private jobScreenCommonService: JobScreenCommonService,
    public inputHelper: InputHelper,
    private activatedRoute: ActivatedRoute,
  ) {
    this.subscriptions.push(
      this.eventService.JobScreenEvent.subscribe((event: any) => {
        this.handleEvents(event);
      }),
    );
  }

  async ngOnInit() {
    void this.initJob(this.jobId);
    await this.initUser();
    this.checkIfShouldAutoOpenSequenceEditor();
  }

  async initJob(jobId: number) {
    this.job = (await this.jobService.getForComponent(
      jobId,
      'job-screen-orders',
    )) as JobScreenOrdersJob;

    if (this.selectedOrder == null) {
      this.selectedOrder = this.job.orders[0];
    } else {
      this.selectedOrder = this.job.orders.find((x) => x.id === this.selectedOrder.id);
    }

    this.checkIfTaskSequencingIsEnabled();
  }

  isOrderCompletedOrCancelled(order: JobScreenOrdersOrder) {
    return order && [OrderStatus.Closed, OrderStatus.Cancelled].some((s) => s == order.statusId);
  }

  getCustomerTags(tags: JobScreenOrdersToOrderTag[]) {
    if (!tags) {
      return '';
    }

    const descriptions = [];

    tags.forEach((x) => {
      descriptions.push(x.orderTag.description);
    });

    return descriptions.join(', ');
  }

  showBookingInfoDialog(order: JobScreenOrdersOrder, event: MouseEvent) {
    //Stop the click on the info icon from also triggering a click event on the order row
    event.stopPropagation();

    this.currentOrder = order;
    this.showBookingInfo = true;
  }

  impersonateCustomer() {
    this.selectedImpersonateUser = null;

    this.orderService.getImpersonateCustomerUsers(this.selectedOrder.id).then((res) => {
      this.impersonateCustomerUsers = this.usersToSelectItems(res);
    });

    this.showImpersonateCustomerDialog = true;
  }

  usersToSelectItems(l) {
    let users = [];
    l.forEach((o) => {
      users.push({
        label:
          o.firstName || o.lastName
            ? ((o.firstName ? o.firstName : '') + ' ' + (o.lastName ? o.lastName : '')).trim()
            : o.email,
        value: o.id,
      });
    });
    return users;
  }

  copyOrder(selectedOrder: JobScreenOrdersOrder) {
    if (!selectedOrder) {
      return;
    }

    //this.inboxBackService.setInbox(this.previousUrl, this.previousInboxType);

    void this.router.navigate(['app/order/copy/' + selectedOrder.id]);
  }

  compareItem(x, y) {
    if (x.name.toLowerCase() > y.name.toLowerCase()) return 1;
    else if (x < y) return -1;
    else return 0;
  }

  isJobCompleted(job: JobScreenOrdersJob): boolean {
    if (!job) {
      return false;
    }

    return (
      job.statusId == JobStatus.Completed ||
      job.statusId == JobStatus.Cancelled ||
      job.statusId == JobStatus.Closed
    );
  }

  isJobInCreatedStatus(job: JobScreenOrdersJob): boolean {
    if (!job) {
      return false;
    }

    return job.statusId == JobStatus.Created;
  }

  isCancelOrderEnableForVME() {
    return (
      this.organisationTypeId == OrganisationTypeEnum.Engineius &&
      this.job?.statusId != JobStatus.Completed &&
      this.job?.statusId != JobStatus.Closed &&
      this.job?.statusId != JobStatus.Cancelled
    );
  }

  async initUser() {
    this.isBusy = true;
    this.currentUser = await this.userService.getLoggedUser();
    this.isImpersonating = (await this.userService.isUserImpersonating()).isImpersonating;
  }

  impersonateCustomerConfirm() {
    this.showImpersonateCustomerDialog = false;
    this.jobScreenCommonService.impersonateUser(this.selectedImpersonateUser);
  }

  ngOnChanges(changes: SimpleChanges): void {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x?.unsubscribe());
  }

  private handleEvents(event: any) {
    if (event.event == 'job-details-changed') {
      void this.initJob(this.jobId);
    }

    if (event.event == 'sequence-update') {
      void this.initJob(this.jobId);
    }
  }

  checkIfTaskSequencingIsEnabled(): void {
    this.orderSequencingEnabled = this.job.orders.some((x) => x.orderSequence != null);

    if (this.orderSequencingEnabled) {
      this.job.orders.forEach((order, index) => {
        order.localOrderSequence = index + 1;
      });
    }
  }

  editTaskSequence(): void {
    this.taskSequenceEditorVisible = true;
  }

  taskSequenceUpdated(): void {
    this.taskSequenceEditorVisible = false;

    this.eventService.JobScreenEvent.emit({
      event: 'sequence-update',
      sender: 'task-sequence',
    });
  }

  onOrderCancelled() {
    this.showCancelOrderDialog = false;
    this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
    this.eventService.JobScreenEvent.emit({
      event: 'job-details-changed',
      data: { jobId: this.jobId },
      sender: 'vme-cancel-order',
    });
  }

  jobHasOrdersWithInspectAndCollect(): boolean {
    return this.job?.orders?.some((order) =>
      [TaskType.DrivenPlatedInspection, TaskType.DrivenInsuredInspection].includes(
        order.taskTypeId,
      ),
    );
  }

  public get allOrderTags() {
    const orders = this.job?.orders || [];
    return flatMap(orders, (order) => order.orderTags || []);
  }

  public get allPurchaseNumbers() {
    const orders = this.job?.orders || [];
    return orders.map((order) => order.purchaseOrderNo).filter((x) => x);
  }

  public get anyOrderCancelled() {
    const orders = this.job?.orders || [];
    return orders.some((order) => order.statusId == OrderStatus.Cancelled);
  }

  asOrder(order: any): JobScreenOrdersOrder {
    return order as JobScreenOrdersOrder;
  }

  async onSaveEditOrder(job: JobScreenOrdersJob) {
    await this.initJob(job.id);
  }

  checkIfShouldAutoOpenSequenceEditor(): void {
    const queryParamsSub = this.activatedRoute.queryParams.subscribe((params) => {
      const openEditorParam = params['openSequenceEditor'];

      if (openEditorParam === 'true') {
        this.directedFromCombine = true;
        this.editTaskSequence();
      }
    });

    this.subscriptions.push(queryParamsSub);
  }

  async createOrderInvoice(selectedOrder: JobScreenOrdersOrder) {
    try {
      this.creatingInvoice = true;
      await this.orderService.createOrderQuoteInvoice(selectedOrder.orderQuote.guid);
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Invoice created successfully',
      });
      void this.initJob(this.jobId);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Invoice could not be created',
      });
    } finally {
      this.creatingInvoice = false;
    }
  }

  onQuickCopyClick(selectedOrder: JobScreenOrdersOrder) {
    this.quickCopyOrder = selectedOrder;
    this.showQuickCopyOrderDialog = true;
  }

  async quickCopy(selectedOrder: JobScreenOrdersOrder) {
    this.loadingQuickCopy = true;
    const result = await this.orderService.quickCopy(selectedOrder.id);
    this.loadingQuickCopy = false;
    this.showQuickCopyOrderDialog = false;
    window.open(`/app/jobscreen/${result.jobId}`, '_blank');
  }
}
